import { useState, useEffect } from 'react';
import { Handle, Position } from '@xyflow/react';
import { getDeviceContacts } from '../../../api/connections';
import type { DeviceContact } from '../../../types/connections';

interface MainSchemaActuatorNodeData {
  deviceName: string;
  deviceType: string;
  deviceTypeId?: number;
  specName: string;
  description: string;
  deviceInstanceId: number;
  specificationId?: number;
  contacts: DeviceContact[];
  knxAddress?: string; // KNX address for actuators
  onRemoveDevice?: (layoutId: number) => void;
  onEditInstance?: (layoutId: number, deviceInstanceId: number) => void;
  layoutId: number;
  isReserve?: boolean; // true если элемент резервный
  manufacturer?: string;
  model?: string;
  schemaType?: string; // Legacy: Тип схемы для правильной загрузки контактов
  schemaId?: number; // ID конкретной схемы для фильтрации контактов
      // isConnecting?: boolean; - TEMPORARILY COMMENTED
}

interface MainSchemaActuatorNodeProps {
  data: MainSchemaActuatorNodeData;
}

/**
 * MAIN SCHEMA: Specialized node component for actuators on main electrical schemas
 * Features:
 * - Only power contacts (4i, 4o, 14i, 14o, etc.)
 * - Top/Bottom positioning based on contact_position in DB
 * - Sorted by contact number
 * - Special color scheme for actuators
 */
export default function MainSchemaActuatorNode({ data }: MainSchemaActuatorNodeProps) {
  const {
    deviceName,
    deviceTypeId,
    specName,
    description,
    deviceInstanceId,
    contacts: initialContacts = [],
    knxAddress,
    isReserve = false,
    manufacturer,
    model,
    // isConnecting = false - TEMPORARILY COMMENTED
  } = data;

  const [contacts, setContacts] = useState<DeviceContact[]>([]);
  const [loading, setLoading] = useState(false);
  const disableAutoLoadContacts = Boolean((data as any).disableAutoLoadContacts);

  // Color scheme for actuators
  const actuatorColors = {
    border: isReserve ? 'border-gray-600' : 'border-purple-600',
    bg: isReserve ? 'bg-gray-50' : 'bg-purple-50',
    bgSecondary: isReserve ? 'bg-gray-100' : 'bg-purple-100',
    borderSecondary: isReserve ? 'border-gray-200' : 'border-purple-200',
    text: isReserve ? 'text-gray-700' : 'text-purple-700',
    textPrimary: isReserve ? 'text-gray-800' : 'text-purple-800',
    textSecondary: isReserve ? 'text-gray-600' : 'text-purple-600',
    handleBg: isReserve ? 'bg-gray-100' : 'bg-purple-100',
    handleBorder: isReserve ? 'border-gray-500' : 'border-purple-500'
  };

  // Sync contacts state with initialContacts prop
  useEffect(() => {
    if (initialContacts && initialContacts.length > 0) {
      setContacts(initialContacts);
    }
  }, [initialContacts, deviceName]);

  // Load contacts asynchronously - always load to ensure all positions are available
  useEffect(() => {
    if (deviceInstanceId && data.specificationId && !disableAutoLoadContacts) {
      const loadContacts = async () => {
        try {
          setLoading(true);
          // Always load fresh contacts to ensure we have all positions (including left/right)
          const specId = data.specificationId!;
          // Используем schemaId (приоритет) или schemaType (legacy)
          const deviceContacts = await getDeviceContacts(specId, undefined, data.schemaId);
          if (deviceContacts && deviceContacts.length > 0) {
            setContacts(deviceContacts);
          } else if (!initialContacts || initialContacts.length === 0) {
            setContacts([]);
          }
        } catch (error) {
          console.error('Error loading actuator contacts:', error);
          // Fallback to initialContacts if load fails
          if (initialContacts && initialContacts.length > 0) {
            setContacts(initialContacts);
          }
        } finally {
          setLoading(false);
        }
      };

      loadContacts();
    }
  }, [deviceInstanceId, data.specificationId, data.schemaType, initialContacts, disableAutoLoadContacts]);

  // Filter only power contacts (single_line type)
  const powerContacts = contacts;

  // Separate contacts by position: top, bottom, left, right
  const topContacts = powerContacts
    .filter(c => c.contact_position === 'top')
    .sort((a, b) => {
      const numA = parseInt(String(a.contact_number));
      const numB = parseInt(String(b.contact_number));
      return numA - numB;
    });

  const bottomContacts = powerContacts
    .filter(c => c.contact_position === 'bottom')
    .sort((a, b) => {
      const numA = parseInt(String(a.contact_number));
      const numB = parseInt(String(b.contact_number));
      return numA - numB;
    });

  const leftContacts = powerContacts
    .filter(c => c.contact_position === 'left')
    .sort((a, b) => {
      const numA = parseInt(String(a.contact_number));
      const numB = parseInt(String(b.contact_number));
      return numA - numB;
    });

  const rightContacts = powerContacts
    .filter(c => c.contact_position === 'right')
    .sort((a, b) => {
      const numA = parseInt(String(a.contact_number));
      const numB = parseInt(String(b.contact_number));
      return numA - numB;
    });

  const contactsCount = Math.max(topContacts.length, bottomContacts.length);
  const sideContactsCount = Math.max(leftContacts.length, rightContacts.length);
  const sideContactsHeight = sideContactsCount > 0 ? sideContactsCount * 22 + 10 : 0; // ~22px per contact + padding
  const sidePanelWidth = 40; // Width for left/right contact panels (increased for side labels)
  const actuatorWidth = contactsCount <= 4 ? 120 : Math.max(120, contactsCount * 24 + 40);
  
  const hasManufacturerInfo = manufacturer || model;
  const actuatorMinHeight = hasManufacturerInfo ? '110px' : '85px';

  if (loading) {
    return (
      <div className={`bg-white border-2 ${actuatorColors.border} rounded shadow-lg p-2`} style={{ minWidth: '120px', minHeight: '85px' }}>
        <div className="text-center text-gray-500 text-xs">Loading actuator...</div>
      </div>
    );
  }

  return (
    <div 
      className={`bg-white border-2 ${actuatorColors.border} rounded shadow-lg select-none flex`}
      style={{ 
        minWidth: actuatorWidth + (leftContacts.length > 0 ? sidePanelWidth : 0) + (rightContacts.length > 0 ? sidePanelWidth : 0),
        minHeight: Math.max(parseInt(actuatorMinHeight), sideContactsHeight || parseInt(actuatorMinHeight)),
        width: 'fit-content'
      }}
    >
      {/* LEFT contacts panel */}
      {leftContacts.length > 0 && (
        <div className="flex flex-col justify-center items-start border-r border-gray-200 py-1 px-1" style={{ width: sidePanelWidth, minHeight: '100%' }}>
          {leftContacts.map((contact) => (
            <div key={contact.id} className="flex items-center relative py-0.5 w-full">
              <div className={`w-3 h-3 rounded-full border-2 ${actuatorColors.handleBorder} ${actuatorColors.handleBg} relative flex-shrink-0`}>
                {/* Target handle для входящих соединений */}
                <Handle
                  type="target"
                  position={Position.Left}
                  id={`${contact.id}-target`}
                  className="schema-target-handle !w-px !h-px !bg-white !border-0"
                  style={{ left: '50%', transform: 'translate(-50%, -50%)' }}
                />
                {/* Source handle для исходящих соединений */}
                <Handle
                  type="source"
                  position={Position.Left}
                  id={`${contact.id}-source`}
                  className="schema-source-handle !w-px !h-px !bg-white !border-0"
                  style={{ left: '50%', transform: 'translate(-50%, -50%)' }}
                />
              </div>
              <div className={`text-xs font-medium ${actuatorColors.text} ml-1`} style={{ fontSize: '8px', whiteSpace: 'nowrap' }}>
                {contact.contact_label || `C${contact.contact_number}`}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Main content area */}
      <div className="flex-1 flex flex-col">
        {/* TOP contacts with handles - TARGET и SOURCE для актуаторов сверху (соединения между собой) */}
        {topContacts.length > 0 && (
          <div className={`flex justify-between items-center px-2 py-1 border-b ${actuatorColors.borderSecondary} gap-1`}>
            {topContacts.map((contact) => (
              <div key={`${contact.id}-top`} className="flex flex-col items-center relative">
                <div className={`w-3 h-3 rounded-full border-2 ${actuatorColors.handleBorder} ${actuatorColors.handleBg} relative`}>
                  {/* Target handle для входящих соединений */}
                  <Handle
                    type="target"
                    position={Position.Top}
                    id={`${contact.id}-target`}
                    className="schema-target-handle !w-px !h-px !bg-white !border-0"
                    // Center inside visual dot for precise anchoring
                    style={{ top: '50%', transform: 'translate(-50%, -50%)' }}
                  />
                  {/* Source handle для исходящих соединений (соединения между TOP контактами) */}
                  <Handle
                    type="source"
                    position={Position.Top}
                    id={`${contact.id}-source`}
                    className="schema-source-handle !w-px !h-px !bg-white !border-0"
                    // Center inside visual dot for precise anchoring
                    style={{ top: '50%', transform: 'translate(-50%, -50%)' }}
                  />
                </div>
                <div className={`text-xs font-medium ${actuatorColors.text} mt-0.5`} style={{ fontSize: '9px' }}>
                  {contact.contact_label || `C${contact.contact_number}`}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Position label (позиционное обозначение) */}
        <div className={`px-2 py-1 text-center border-b ${actuatorColors.borderSecondary}`}>
          <div className={`font-bold ${actuatorColors.textPrimary}`} style={{ fontSize: '11px', wordBreak: 'break-all' }}>
            {deviceName}
          </div>
          {/* Specification name (название родителя) - под позиционным обозначением */}
          <div className={`font-bold ${actuatorColors.text}`} style={{ fontSize: '10px', wordBreak: 'break-all', marginTop: '2px' }}>
            {specName}
          </div>
        </div>

        {/* KNX Address - for KNX devices (device_type_id = 14) and Actuators (device_type_id = 5) */}
        {(deviceTypeId === 14 || deviceTypeId === 5) && (
          <div className={`px-2 py-1 text-center border-b ${actuatorColors.borderSecondary}`}>
            <div className="text-gray-500" style={{ fontSize: '10px' }}>
              {knxAddress || '—'}
            </div>
          </div>
        )}

        {/* Manufacturer and Model - только для актуаторов */}
        {(manufacturer || model) && (
          <div className={`px-2 py-1 text-center border-b ${actuatorColors.borderSecondary} ${actuatorColors.bgSecondary}`}>
            <div className={`font-bold ${actuatorColors.text}`} style={{ fontSize: '10px', wordBreak: 'break-all' }}>
              {manufacturer && <span>{manufacturer}</span>}
              {manufacturer && model && <span> | </span>}
              {model && <span>{model}</span>}
            </div>
          </div>
        )}

        {/* Device description */}
        <div className="px-1 py-1 text-center border-b border-gray-200" style={{ minHeight: '35px' }}>
          <div className={`font-medium ${actuatorColors.textSecondary} flex items-center justify-center h-full`} style={{ fontSize: '9px' }}>
            {description && description.trim() ? description : ''}
          </div>
        </div>

        {/* BOTTOM contacts with handles - TARGET и SOURCE для актуаторов снизу (соединения между собой) */}
        {bottomContacts.length > 0 && (
          <div className="flex justify-between items-center px-2 py-1 gap-1">
            {bottomContacts.map((contact) => (
              <div key={`${contact.id}-bottom`} className="flex flex-col items-center relative">
                {/* Contact label above the handle */}
                <div className={`text-xs font-medium ${actuatorColors.text} mb-0.5`} style={{ fontSize: '9px' }}>
                  {contact.contact_label || `C${contact.contact_number}`}
                </div>
                {/* Handle at the bottom */}
                <div className={`w-3 h-3 rounded-full border-2 ${actuatorColors.handleBorder} ${actuatorColors.handleBg} relative`}>
                  {/* Target handle для входящих соединений */}
                  <Handle
                    type="target"
                    position={Position.Bottom}
                    id={`${contact.id}-target`}
                    className="schema-target-handle !w-px !h-px !bg-white !border-0"
                    // Center inside visual dot for precise anchoring
                    style={{ bottom: '50%', transform: 'translate(-50%, 50%)' }}
                  />
                  {/* Source handle для исходящих соединений (соединения между BOTTOM контактами) */}
                  <Handle
                    type="source"
                    position={Position.Bottom}
                    id={`${contact.id}-source`}
                    className="schema-source-handle !w-px !h-px !bg-white !border-0"
                    // Center inside visual dot for precise anchoring
                    style={{ bottom: '50%', transform: 'translate(-50%, 50%)' }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* RIGHT contacts panel */}
      {rightContacts.length > 0 && (
        <div className="flex flex-col justify-center items-end border-l border-gray-200 py-1 px-1" style={{ width: sidePanelWidth, minHeight: '100%' }}>
          {rightContacts.map((contact) => (
            <div key={contact.id} className="flex items-center relative py-0.5 w-full justify-end">
              <div className={`text-xs font-medium ${actuatorColors.text} mr-1`} style={{ fontSize: '8px', whiteSpace: 'nowrap' }}>
                {contact.contact_label || `C${contact.contact_number}`}
              </div>
              <div className={`w-3 h-3 rounded-full border-2 ${actuatorColors.handleBorder} ${actuatorColors.handleBg} relative flex-shrink-0`}>
                {/* Target handle для входящих соединений */}
                <Handle
                  type="target"
                  position={Position.Right}
                  id={`${contact.id}-target`}
                  className="schema-target-handle !w-px !h-px !bg-white !border-0"
                  style={{ right: '50%', transform: 'translate(50%, -50%)' }}
                />
                {/* Source handle для исходящих соединений */}
                <Handle
                  type="source"
                  position={Position.Right}
                  id={`${contact.id}-source`}
                  className="schema-source-handle !w-px !h-px !bg-white !border-0"
                  style={{ right: '50%', transform: 'translate(50%, -50%)' }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
} 